import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Base } from "../../framework/base";
import {
    getVehiclePositions,
    getVehicles,
} from "../../services/vehicleService";
import { useFetch } from "../../hooks/useFetch";
import { useInterval } from "usehooks-ts";
import { WorkShiftDto } from "../../models/workShift/workShift";
import { getCurrentWorkShift, getWorkShiftsNew } from "../../services/workShiftService";
import { handleApiError } from "../../models/store/storeEffects";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import { useReload } from "../../hooks";
import { MonitorViewMainLayout } from "./monitorViewMainLayout";
import { fetchEmployees as getEmployees } from "../../store/employeeSlice";
import { TabsEnum } from "./monitorViewUtils";
import {
    setSelectedEmployeeId,
    setSelectedWorkShiftTimeSlotId,
    setSelectedWorkShift,
    setSidebarTitle,
    toggleWorkShiftView,
    setVehicles,
    setPositions,
} from "../../store/monitorViewSlice";
import { fetchWorkTimeTypes, setWorkTimeTypeIds } from "../../store/monitorViewFiltersSlice";
import { handleVehicleSelect } from "./monitorViewUtils";
import { useTranslation } from "react-i18next";

const refreshInterval = 60 * 1000; // 1 minute

export const MonitorViewMain = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const [positionsLoading, setPositionsLoading] = useState(true);
    const [workShiftLoading, setWorkShiftLoading] = useState(false);
    const [workShiftsLoading, setWorkShiftsLoading] = useState(true);
    const workTimeTypesLoading = useAppSelector(
        (state) => state.monitorViewFilters.workTimeTypesLoading
    );
    const loading = positionsLoading || workShiftLoading || workShiftsLoading || workTimeTypesLoading;
    const [key, reload] = useReload();

    const workTimeTypes = useAppSelector((state) => state.monitorViewFilters.workTimeTypes);
    const hasInitializedFilters = useRef(false);

    const [workShifts, setWorkShifts] = useState<WorkShiftDto[]>([]);

    const employees = useAppSelector(
        (state) => state.employees.employees
    );

    const {
        selectedVehicleId,
        selectedWorkShiftTimeSlotId,
        selectedEmployeeId,
        selectedWorkShift: workShift,
        activeTab: tab,
        vehicles,
        positions,
        tripStartDate,
        isVehicleRouteView,
    } = useAppSelector((state) => state.monitorView);

    const employeeId = useMemo(() => {
        if (selectedEmployeeId) {
            return positions?.find((p) =>
                p.employeeWorkTimeTypes.some((e) => e.employeeId === selectedEmployeeId)
            )?.employeeWorkTimeTypes[0]?.employeeId || null;
        }

        if (selectedVehicleId && tab === TabsEnum.EMPLOYEES) {
            return positions?.find((p) => p.vehicleId === selectedVehicleId)
                ?.employeeWorkTimeTypes[0]?.employeeId || null;
        }

    }, [positions, selectedEmployeeId, tab]);

    const employeeIds = useMemo(() => {
        const ids = new Set<string>();

        positions?.forEach((p) => {
            p.employeeWorkTimeTypes.forEach((e) => {
                ids.add(e.employeeId);
            });
        });

        return Array.from(ids);
    }, [positions]);

    const fetchWorkShifts = useCallback(
        (showLoadingIndicator = false) => {
            if (employeeIds.length === 0) return;

            if (showLoadingIndicator) {
                setPositionsLoading(true);
            }

            const now = new Date();
            const startDate = Base.dateStartOf(now, "month").format("YYYY-MM-DD");
            const endDate = Base.dateEndOf(now, "month").format("YYYY-MM-DD");

            getWorkShiftsNew(employeeIds, startDate, endDate, true)
                .then((res) => {
                    const filteredWorkShifts = res?.data
                        ?.filter((shift) => shift.endTime === null)
                        ?.map((shift) => ({
                            ...shift,
                            workShiftTimeSlots: shift.workShiftTimeSlots.filter(
                                (slot) => slot.endDate === null
                            ),
                        })) || [];

                    setWorkShifts(filteredWorkShifts);
                    if (showLoadingIndicator) {
                        setPositionsLoading(false);
                    }
                })
                .catch((e) => {
                    if (showLoadingIndicator) {
                        setPositionsLoading(false);
                    }
                    handleApiError(e, dispatch);
                });
        },
        [employeeIds]
    );

    useEffect(() => {
        if (!workTimeTypes) {
            dispatch(fetchWorkTimeTypes() as any);
        }
    }, [dispatch, workTimeTypes]);

    // Automatically toggle all filters by default when workTimeTypes are loaded
    useEffect(() => {
        if (workTimeTypes && !hasInitializedFilters.current) {
            const allWorkTimeTypeIds = workTimeTypes.map((type) => type.workTimeTypeId);
            dispatch(setWorkTimeTypeIds(allWorkTimeTypeIds));
            hasInitializedFilters.current = true;
        }
    }, [dispatch, workTimeTypes]);

    useEffect(() => {
        fetchWorkShifts();
    }, [fetchWorkShifts]);

    const handleSelect = (
        id: string,
        selectedId: string | null,
        setSelectedId: (id: string | null) => void,
        resetWorkShift: boolean = true) =>
    {
        if (resetWorkShift) {
            dispatch(setSelectedWorkShift(null));
            dispatch(setSelectedWorkShiftTimeSlotId(null));
        }

        if (id !== selectedId) {
            setSelectedId(id);
        } else {
            setSelectedId(null);
        }
    };

    const onVehicleSelect = (vehicleId: string) => {
        handleVehicleSelect(
            vehicleId,
            vehicles,
            tripStartDate,
            isVehicleRouteView,
            dispatch,
            t
        );
    };

    const findEmployeeIdFromPositions = (id: string) => {
        return positions?.find((p) => p.vehicleId === id)
            ?.employeeWorkTimeTypes[0]?.employeeId || null;
    };

    const onEmployeeSelect = (vehicleId: string) => {
        const employeeId = findEmployeeIdFromPositions(vehicleId);
        handleSelect(employeeId, selectedEmployeeId, (employeeId) => dispatch(setSelectedEmployeeId(employeeId)));

        getCurrentWorkShift(employeeId)
            .then((workShift) => {
                dispatch(setSelectedWorkShift(workShift));
                dispatch(setSidebarTitle(workShift?.workShiftTimeSlots?.[0]?.employeeName || ""));
                dispatch(toggleWorkShiftView());
            })
            .catch((e) => {
                handleApiError(e, dispatch);
            });
    };

    const onWorkShiftTimeSlotSelect = (id: string) => {
        handleSelect(id, selectedWorkShiftTimeSlotId, (id) => dispatch(setSelectedWorkShiftTimeSlotId(id)), false);
    };

    useFetch(
        (signal) => getVehicles(false, signal),
        {
            onSuccess: (res) => void dispatch(setVehicles(res.data)),
            withLoadingIndicator: false,
        },
        []
    );

    useEffect(() => {
        if (!!positions) setPositionsLoading(false);
    }, [positions]);

    useEffect(() => {
        if (!!workShift) setWorkShiftLoading(false);
    }, [workShift]);

    useEffect(() => {
        if (!!workShifts) setWorkShiftsLoading(false);
    }, [workShifts]);

    const fetchLocations = useCallback(
        (showLoadingIndicator = false) => {
            if (!selectedVehicleId && vehicles?.length > 0) {
                if (showLoadingIndicator) {
                    setPositionsLoading(true);
                }

                getVehiclePositions(
                    selectedVehicleId
                        ? [selectedVehicleId]
                        : vehicles.map((v) => v.id),
                    true
                )
                    .then((res) => {
                        dispatch(setPositions(res?.data));
                    })
                    .catch((e) => {
                        if (showLoadingIndicator) {
                            setPositionsLoading(false);
                        }
                        handleApiError(e, dispatch);
                    });
            }
        },
        [vehicles, selectedVehicleId]
    );

    const fetchWorkShift = useCallback(
        (showLoadingIndicator = false) => {
            if (!!employeeId) {
                if (showLoadingIndicator) {
                    setWorkShiftLoading(true);
                }

                getCurrentWorkShift(employeeId, true)
                    .then((res) => dispatch(setSelectedWorkShift(res)))
                    .catch((e) => {
                        if (showLoadingIndicator) {
                            setWorkShiftLoading(false);
                        }
                        handleApiError(e, dispatch);
                    });
            }
        },
        [employeeId]
    );

    const fetchEmployees = useCallback(() => {
        if (employees.length === 0) {
            dispatch(getEmployees() as any);
        }
    }, [employees]);

    const onRefresh = (showLoadingIndicator = false) => {
        fetchLocations(showLoadingIndicator);
        fetchWorkShift(showLoadingIndicator);
        reload();
    };

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    useEffect(() => {
        fetchWorkShift();
    }, [fetchWorkShift]);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    useInterval(onRefresh, refreshInterval);

    return (
        <MonitorViewMainLayout
            positions={positions}
            loading={loading}
            workShift={workShift}
            workShifts={workShifts}
            selectedVehicleId={selectedVehicleId}
            selectedEmployeeId={selectedEmployeeId}
            reloadKey={key}
            onRefresh={onRefresh}
            setSelectedVehicleId={onVehicleSelect}
            setSelectedWorkShiftTimeSlotId={onWorkShiftTimeSlotSelect}
            setSelectedEmployeeId={onEmployeeSelect}
        />
    );
};